/**
 * Gallery — public marketing showcase page (marketing zone, no auth).
 *
 * Route: /gallery
 *
 * One page that shows a signed-out visitor the value they're missing:
 *   1. Feature showcase — the good things you only get with an account
 *      (trip builder, favorites, sharing, dashboard), each with a sign-up CTA.
 *   2. Photo gallery — real trip imagery.
 *   3. Traveler reviews — real published testimonials.
 *   4. Closing call to action.
 *
 * Reuses existing services + helpers (tripsService, reviewsService,
 * getTripImageUrl) and the shared auth modal, so nothing here is bespoke plumbing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardMedia,
  Stack,
  Avatar,
  Rating,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  EditRoad,
  FavoriteBorder,
  Share,
  DashboardCustomize,
  ArrowForward,
  Explore,
  Lock,
  FormatQuote,
} from '@mui/icons-material';
import ButtonPill from '../ui/ButtonPill';
import { useAuth } from '../../context/AuthContext';
import { useAuthModal } from '../../context/AuthModalContext';
import tripsService from '../../services/tripsService';
import reviewsService from '../../services/reviewsService';
import { getTripImageUrl } from '../../utils/tripImage';

const FEATURES = [
  {
    icon: EditRoad,
    title: 'Build your own trip',
    body: 'Design a day-by-day itinerary with flights, stays, activities and live budgeting — your trip, exactly how you want it.',
    color: '#2E7D32',
  },
  {
    icon: FavoriteBorder,
    title: 'Save your favorites',
    body: 'Bookmark the trips and destinations you love and come back to them anytime from your dashboard.',
    color: '#C2185B',
  },
  {
    icon: Share,
    title: 'Share your plans',
    body: 'Send a beautiful read-only link of your itinerary to travel companions, or request a custom quote from our team.',
    color: '#1976D2',
  },
  {
    icon: DashboardCustomize,
    title: 'Track everything',
    body: 'Your bookings, plans, requests and reviews all live in one place, so nothing about your journey gets lost.',
    color: '#E65100',
  },
];

const Gallery = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();

  const [trips, setTrips] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [tripsRes, reviewsRes] = await Promise.all([
          tripsService.getTrips({ per_page: 12 }).catch(() => ({ trips: [] })),
          reviewsService.getShowcaseReviews(9).catch(() => ({ reviews: [] })),
        ]);
        if (!active) return;
        const tripList = (tripsRes?.trips ?? []).filter((t) => getTripImageUrl(t));
        setTrips(tripList.slice(0, 8));
        setReviews(reviewsRes?.reviews ?? []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Guests are nudged to sign up; members go straight to the app.
  const goStart = useCallback(
    (returnTo = '/user/dashboard') => {
      if (isAuthenticated) {
        navigate(returnTo);
      } else {
        openAuthModal({ initialMode: 'signup', title: 'Create your free account', returnTo });
      }
    },
    [isAuthenticated, navigate, openAuthModal]
  );

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* ═══ Hero ═══ */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          color: 'white',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.15)',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontFamily: '"Playfair Display", serif',
              fontSize: { xs: '2.2rem', sm: '3rem', md: '3.6rem' },
              textShadow: '2px 2px 12px rgba(0,0,0,0.3)',
              mb: 2,
            }}
          >
            There's a whole trip waiting inside
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.95, mb: 4, maxWidth: 620, mx: 'auto' }}>
            Real journeys, real travelers, and the tools to plan your own. Here's a look at what you
            unlock with a free Royal Dastinos account.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <ButtonPill
              variant="contained"
              size="large"
              startIcon={<ArrowForward />}
              onClick={() => goStart()}
              sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: 'grey.100' } }}
            >
              {isAuthenticated ? 'Go to your dashboard' : 'Create your free account'}
            </ButtonPill>
            <ButtonPill
              variant="outlined"
              size="large"
              startIcon={<Explore />}
              onClick={() => navigate('/trips')}
              sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Explore trips
            </ButtonPill>
          </Stack>
        </Container>
      </Box>

      {/* ═══ Feature showcase (the gated value) ═══ */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif', mb: 1.5, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
            Do more with an account
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
            Signing up is free. It unlocks everything you need to plan, save and book your perfect trip.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={f.title}>
                <Paper
                  elevation={0}
                  onClick={() => goStart()}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 4,
                    border: `2px solid ${theme.palette.grey[200]}`,
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: theme.shadows[8],
                      borderColor: alpha(f.color, 0.5),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(f.color, 0.12),
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ color: f.color, fontSize: 28 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {f.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, mb: 2 }}>
                    {f.body}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: theme.palette.primary.main, fontWeight: 700 }}>
                    {!isAuthenticated && <Lock sx={{ fontSize: 15 }} />}
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {isAuthenticated ? 'Open' : 'Sign up to unlock'}
                    </Typography>
                    <ArrowForward sx={{ fontSize: 15 }} />
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* ═══ Photo gallery ═══ */}
      {(loading || trips.length > 0) && (
        <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04), py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif', mb: 1.5, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                A glimpse of the journeys
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
                Destinations our travelers have explored. Tap any to see the full trip.
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {trips.map((trip, i) => (
                  <Grid item xs={6} md={3} key={trip.id ?? i}>
                    <Card
                      onClick={() => navigate(`/trips/${trip.id}`)}
                      sx={{
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        aspectRatio: '1 / 1',
                        '&:hover .gallery-overlay': { opacity: 1 },
                        '&:hover img': { transform: 'scale(1.08)' },
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={getTripImageUrl(trip)}
                        alt={trip.title}
                        sx={{ height: '100%', width: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
                      />
                      <Box
                        className="gallery-overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'flex-end',
                          p: 1.5,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 55%)',
                          opacity: { xs: 1, md: 0 },
                          transition: 'opacity 0.3s ease',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                          {trip.title}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>
        </Box>
      )}

      {/* ═══ Traveler reviews ═══ */}
      {reviews.length > 0 && (
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif', mb: 1.5, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
              What our travelers say
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
              Real reviews from real Royal Dastinos journeys.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {reviews.map((r) => (
              <Grid item xs={12} sm={6} md={4} key={r.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 4,
                    border: `2px solid ${theme.palette.grey[200]}`,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                  }}
                >
                  <FormatQuote sx={{ position: 'absolute', top: 16, right: 16, fontSize: 40, color: alpha(theme.palette.primary.main, 0.12) }} />
                  <Rating value={Number(r.rating) || 0} readOnly size="small" sx={{ mb: 1.5 }} />
                  {r.title && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {r.title}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, mb: 2 }}>
                    {r.comment}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40, fontSize: '0.9rem', fontWeight: 700 }}>
                      {r.user_initials || (r.reviewer_name || '?').charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {r.reviewer_name || 'Traveler'}
                      </Typography>
                      {r.trip?.title && (
                        <Typography variant="caption" color="text.secondary">
                          {r.trip.title}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* ═══ Closing CTA ═══ */}
      <Box sx={{ bgcolor: theme.palette.primary.main, color: 'white', py: { xs: 6, md: 9 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif', mb: 1.5, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
            Ready to start your journey?
          </Typography>
          <Typography sx={{ opacity: 0.95, mb: 4, maxWidth: 520, mx: 'auto' }}>
            Create your free account and start building, saving and booking in minutes.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <ButtonPill
              variant="contained"
              size="large"
              startIcon={<ArrowForward />}
              onClick={() => goStart()}
              sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: 'grey.100' } }}
            >
              {isAuthenticated ? 'Go to your dashboard' : 'Create your free account'}
            </ButtonPill>
            <ButtonPill
              variant="outlined"
              size="large"
              onClick={() => navigate('/trips')}
              sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Browse trips
            </ButtonPill>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Gallery;
